import { i18n } from '../i18n/i18n.js'

import { PRODUCT_ATTRIBUTE_IDS } from '../constants/productAttributes.js'
import { COLOR_MODE } from '../constants/colorMode.js'
import { formatProductPrice } from '../utils/priceUtils.js'

import { SwatchValue } from '../ui/SwatchValue/SwatchValue.jsx'

/**
 * Baseline order of fields for Product Preview cards (Popover / BottomSheet)
 */
export const DEFAULT_PREVIEW_ITEMS = [
  PRODUCT_ATTRIBUTE_IDS.BUILDING,
  PRODUCT_ATTRIBUTE_IDS.TIERS,
  PRODUCT_ATTRIBUTE_IDS.ROOMS,
  PRODUCT_ATTRIBUTE_IDS.AREA,
  PRODUCT_ATTRIBUTE_IDS.PRICE,
]

/**
 * UI Map for Product Preview fields.
 * Extends raw values with Swatch indicators when colorMode is active.
 */
export const PREVIEW_UI_MAP = {
  [PRODUCT_ATTRIBUTE_IDS.PRICE]: {
    labelKey: 'common.price',
    isSupported: (context) => context?.pricesVisible !== false,
    span: 2,
    render: (p, context) => formatProductPrice(p, context?.pricesVisibility)?.displayPrice ?? null,
  },

  [PRODUCT_ATTRIBUTE_IDS.ROOMS]: {
    render: (product, context) => {
      const { colorMode, roomsVarIndex } = context
      const isRoomsMode = colorMode === COLOR_MODE.ROOMS && roomsVarIndex(product.rooms) != null
      if (isRoomsMode) {
        return (
          <SwatchValue cssVar={`--bim-color-rooms-${roomsVarIndex(product.rooms)}`}>
            {product.rooms}
          </SwatchValue>
        )
      }
      return (product.rooms ?? '—')
    },
  },

  // Items only added if matching color-mode is active (explain color in Popover)
  [PRODUCT_ATTRIBUTE_IDS.INSOLATION_HOURS]: {
    labelKey: 'product.insolation_hours',
    isSupported: (ctx) => ctx.colorMode === COLOR_MODE.INSOLATION,
    render: (product, context) => {
      const { getInsolationBucket, formatCompartmentInsolation } = context
      const bucket = getInsolationBucket(product.sun_light_ranges)
      return (
        <SwatchValue cssVar={`--bim-color-insolation-${bucket}`}>
          {formatCompartmentInsolation(product) ?? '—'}
        </SwatchValue>
      )
    },
  },

  [PRODUCT_ATTRIBUTE_IDS.COMPARE_AREA]: {
    labelKey: 'product.compare_area',
    isSupported: (ctx) => ctx.colorMode === COLOR_MODE.COMPARE && ctx.colorModeDeciles?.has(product.uuid),
    render: (product, context) => {
      const { colorModeDeciles } = context
      const decile = colorModeDeciles.get(product.uuid)
      const rank = parseInt(decile.slice(1), 10) + 1
      return (
        <SwatchValue cssVar={`--bim-color-compare-${decile}`}>
          {rank}/10
        </SwatchValue>
      )
    },
  },

  [PRODUCT_ATTRIBUTE_IDS.CATEGORY]: {
      labelKey: 'product.category',
      span: 2,
      isSupported: (ctx) => ctx.colorMode === COLOR_MODE.CATEGORY,
      render: (product, context) => {
        const { categoryVarKey } = context
        return (
            <SwatchValue cssVar={`--bim-color-category-${categoryVarKey}`}>
            { product.product_category
                ? i18n.t(`product_category_strings.${product.product_category}`, { count: 1 }) 
                : '—'
            }
            </SwatchValue>
        )
      },
    },
}